import { ApiError } from '@/lib/api'
import { uebersetze } from '@/lib/uebersetzen'

/**
 * Aus einem Fehler einen Satz machen, den man lesen kann.
 *
 * Ohne das steht im Panel, was der Browser weiß: „HTTP 500 on
 * /api/v1/users/baiboon/password". Das ist keine Auskunft, sondern eine
 * Fundstelle — sie sagt weder, was schiefging, noch was jetzt zu tun ist.
 * David hat das an einem Tag zweimal zu sehen bekommen (403 beim Löschen
 * eines Kommentars, 500 beim Passwort).
 *
 * `was` benennt die Sache, um die es ging, damit derselbe Satz an mehreren
 * Stellen passt: „Dieser Zugang ist nicht mehr vorhanden." Es kommt übersetzt
 * herein, sonst stünde ein deutsches Wort im englischen Satz.
 */
export function fehlersatz(e: unknown, was?: string): string {
  const status = e instanceof ApiError ? e.status : 0
  const sache = was ?? uebersetze('meldung.was_das')

  if (status === 401) return uebersetze('meldung.sitzung_abgelaufen')
  if (status === 403) return uebersetze('meldung.keine_berechtigung')
  if (status === 404) return uebersetze('meldung.gibt_es_nicht_mehr', { was: sache })
  if (status === 409) return uebersetze('meldung.inzwischen_geaendert', { was: sache })
  // Die Schreib-Bremse des Servers (SchreibBremseMiddleware): ein Mensch
  // trifft sie kaum, und wenn, hilft nur Warten.
  if (status === 429) return uebersetze('meldung.zu_viele_aenderungen')
  if (status === 422 || status === 400) {
    /* Bei einer abgelehnten Eingabe weiß nur der Server, was ihm fehlte —
       seine Meldung ist hier die bessere Auskunft als jeder eigene Satz. */
    return serverSatz(e) ?? uebersetze('meldung.eingabe_abgelehnt')
  }
  if (status >= 500) {
    const dazu = serverSatz(e)
    return dazu === null
      ? uebersetze('meldung.server_stumm')
      : uebersetze('meldung.server_abgebrochen', { dazu })
  }
  return uebersetze('meldung.nicht_geklappt')
}

/**
 * Was der Server selbst dazu sagt.
 *
 * Auf Staging steht dort bei einem Absturz die echte Meldung samt Datei und
 * Zeile (`MIRACMS_DEBUG=true`), live nur „Internal server error". Beides ist
 * besser als eine nackte Zahl: Im ersten Fall steht die Ursache da, im
 * zweiten fällt auf, dass der Server nichts verrät.
 */
function serverSatz(e: unknown): string | null {
  if (!(e instanceof ApiError)) return null
  const body = e.body as { message?: unknown; error?: unknown } | null
  const text = typeof body?.message === 'string'
    ? body.message
    : typeof body?.error === 'string' ? body.error : null
  return text !== null && text.trim() !== '' ? text : null
}
